/** @jsx jsx */
/** @jsxFrag null */
import {jsx} from 'panel';

import {AvatarUploadEl} from 'common/widgets/avatar-upload';
import {TrackedUserIconEl} from 'common/widgets/tracked-user-icon';
import {UserIconEl} from 'common/widgets/user-icon';
import {UserListEl} from 'common/widgets/user-list';
import {UserType} from 'common/widgets/user-list/constants';

import {CodeTipEl} from '../components/code-tip';
import avatarSm from '../../assets/avatars/small.png';
import avatarMd from '../../assets/avatars/medium.png';
import avatarLg from '../../assets/avatars/large.png';
import avatarXl from '../../assets/avatars/xl.png';
import {Section, SubSection} from './utils';

const DATE_IN_SECONDS = new Date().getTime() / 1000;
const HOURS = 60 * 60;

function avatarList(type, avatars, codeTip = true) {
  const IconContainer = codeTip ? CodeTipEl : `div`;
  return (
    <div sel='.avatar-list'>
      {avatars.map((avatarAttrs) => (
        <div sel='.icon-container'>
          <IconContainer class={{example: codeTip}}>
            {type === `tracked-user` ? <TrackedUserIconEl attrs={avatarAttrs} /> : <UserIconEl attrs={avatarAttrs} />}
            <div sel='.name'>{avatarAttrs.displayName || avatarAttrs.name || ``}</div>
          </IconContainer>
        </div>
      ))}
    </div>
  );
}

export function AvatarsSection() {
  return (
    <Section title='Avatars'>
      <SubSection title='Overview' autoDoc={false}>
        <p sel='.desc'>
          Avatars exist to visually show a user without needing to read their name. It’s a great way to make the product
          feel more alive, and as if people are using it, even if they’re not active in the moment.
        </p>
        <h2>Types</h2>
        <p sel='.desc'>
          We have two categories of avatars: <a attrs={{href: `#mp-user-icon`}}>Collaborators</a> &{` `}
          <a attrs={{href: `#mp-tracked-user-icon`}}>Tracked Users</a>
        </p>
      </SubSection>
      <SubSection title='mp-user-icon' autoDoc={true}>
        <p sel='.desc'>
          <strong>Collaborators</strong> are team members that have been invited to a Mixpanel organization. There are
          three possible avatar types for this category.
        </p>
        <ul>
          <li>
            1. <strong>Photo</strong>: collaborator has uploaded a photo
          </li>
          <li>
            2. <strong>Placeholder</strong>: collaborator has not uploaded a photo. We will use the initials of the
            collaborator’s first & last name across a set of 10 different colors.
          </li>
          <li>
            3. <strong>No Name Placeholder</strong>: If a collaborator has not added a name, we will use the placeholder
            shown below.
          </li>
        </ul>
        <div sel='.examples'>
          <div sel='.avatar-example'>
            <h4 sel='.example-title'>Photo</h4>
            {avatarList(`user`, [
              {
                'code-tip': true,
                name: `X-Large `,
                id: 1,
                size: `xl`,
                'img-src': avatarXl,
              },
              {name: `Large`, id: 2, size: `large`, 'img-src': avatarLg},
              {name: `Med`, id: 3, size: `medium`, 'img-src': avatarMd},
              {name: `Small`, id: 4, size: `small`, 'img-src': avatarSm},
            ])}
          </div>

          <div sel='.avatar-example'>
            <h4 sel='.example-title'>Placeholder</h4>
            {avatarList(`user`, [
              {name: `R M`, id: 8, size: `xl`, displayName: `X-Large`},
              {name: `R M`, id: 6, size: `large`, displayName: `Large`},
              {name: `R M`, id: 1, size: `medium`, displayName: `Med`},
              {name: `R M`, id: 2, size: `small`, displayName: `Small`},
            ])}
          </div>

          <div sel='.avatar-example'>
            <h4 sel='.example-title'>No Name Placeholder</h4>
            {avatarList(`user`, [
              {id: 8, size: `xl`, displayName: `X-Large`},
              {id: 6, size: `large`, displayName: `Large`},
              {id: 1, size: `medium`, displayName: `Med`},
              {id: 2, size: `small`, displayName: `Small`},
            ])}
          </div>

          <div sel='.avatar-example'>
            <h4 sel='.example-title'>Colors</h4>
            <div sel='.example.colors'>
              <div sel='.color' style={{'background-color': `#FF9A9A`}}>
                FF9A9A
              </div>
              <div sel='.color' style={{'background-color': `#D2A5FF`}}>
                D2A5FF
              </div>
              <div sel='.color' style={{'background-color': `#FFAA7A`}}>
                FFAA7A
              </div>
              <div sel='.color' style={{'background-color': `#FFD600`}}>
                FFD600
              </div>
              <div sel='.color' style={{'background-color': `#BAAEFF`}}>
                BAAEFF
              </div>
              <div sel='.color' style={{'background-color': `#EAA3D2`}}>
                EAA3D2
              </div>
              <div sel='.color' style={{'background-color': `#CAD3E8`}}>
                CAD3E8
              </div>
              <div sel='.color' style={{'background-color': `#71BBFF`}}>
                71BBFF
              </div>
              <div sel='.color' style={{'background-color': `#83EDDA`}}>
                83EDDA
              </div>
              <div sel='.color' style={{'background-color': `#FF7878`}}>
                FF7878
              </div>
            </div>
          </div>
        </div>
      </SubSection>
      <SubSection title='mp-tracked-user-icon' autoDoc={true}>
        <p sel='.desc'>
          <strong>Tracked Users</strong> are the individuals being tracked in Mixpanel. All tracked users should
          maintain the same avatar across reports in Mixpanel (eg: If a tracked user is blue in Explore, they should
          also be blue in Live View). We will utilize a set of 12 different avatars.
        </p>
        <div sel='.examples'>
          <div sel='.avatar-example'>
            {avatarList(`tracked-user`, [
              {'distinct-id': `distinct-1`, size: `xl`, displayName: `X-Large`},
              {'distinct-id': `distinct-1`, size: `large`, displayName: `Large`},
              {'distinct-id': `distinct-1`, size: `medium`, displayName: `Med`},
              {'distinct-id': `distinct-1`, size: `small`, displayName: `Small`},
            ])}
          </div>

          <div sel='.avatar-example'>
            <div sel='.example'>
              {avatarList(
                `tracked-user`,
                [
                  {'distinct-id': `distinct-2`, size: `small`},
                  {'distinct-id': `distinct-1`, size: `small`},
                  {'distinct-id': `distinct-3`, size: `small`},
                  {'distinct-id': `distinct-4`, size: `small`},
                  {'distinct-id': `distinct-5`, size: `small`},
                  {'distinct-id': `distinct-6`, size: `small`},
                  {'distinct-id': `distinct-15`, size: `small`},
                  {'distinct-id': `distinct-20`, size: `small`},
                  {'distinct-id': `distinct-23`, size: `small`},
                  {'distinct-id': `distinct-31`, size: `small`},
                  {'distinct-id': `distinct-44`, size: `small`},
                  {'distinct-id': `distinct-12`, size: `small`},
                ],
                false,
              )}
            </div>
          </div>
        </div>
      </SubSection>
      <SubSection title='mp-user-list' autoDoc={true}>
        <p sel='.desc'>
          When placing multiple photos together in a bundle, use a <strong>User List</strong> to showcase up to three
          full photos, with one additional spot for an overflow message.
        </p>
        <div sel='.examples'>
          <div sel='.avatar-example'>
            <h4 sel='.example-title'>Photo</h4>
            <CodeTipEl sel='.example'>
              <UserListEl
                attrs={{
                  users: [
                    {id: 1, name: `First User`, 'img-src': avatarXl},
                    {id: 2, name: `Second User`, 'img-src': avatarLg},
                    {id: 3, name: `Third User`, 'img-src': avatarMd},
                    {id: 4, name: `Fourth User`, 'img-src': avatarSm},
                    {id: 5, name: `Other User`},
                  ],
                  'max-icons-displayed': 4,
                }}
              />
            </CodeTipEl>
          </div>
          <div sel='.avatar-example'>
            <h4 sel='.example-title'>With limits</h4>
            <CodeTipEl sel='.example'>
              <UserListEl
                attrs={{
                  users: [
                    {id: 1, name: `First User`, 'img-src': avatarXl},
                    {id: 2, name: `Second User`, 'img-src': avatarLg},
                    {id: 3, name: `Third User`, 'img-src': avatarMd},
                    {id: 4, name: `Fourth User`, 'img-src': avatarSm},
                    {id: 5, name: `Other User`},
                  ],
                  'max-icons-displayed': 1,
                  'max-icons-total': 2,
                }}
              />
            </CodeTipEl>
          </div>
          <div sel='.avatar-example'>
            <h4 sel='.example-title'>Placeholder</h4>
            <CodeTipEl sel='.example'>
              <UserListEl
                attrs={{
                  users: [
                    {id: 1, name: `Helga Pataki`},
                    {id: 2, name: `Gerald Johanssen`},
                    {id: 3, name: `Arnold Shortman`},
                    {id: 4, name: `Harold Berman`},
                    {id: 5, name: `Eugene Horowitz`},
                  ],
                  'max-icons-displayed': 4,
                }}
              />
            </CodeTipEl>
          </div>
          <div sel='.avatar-example'>
            <h4 sel='.example-title'>Tracked Users</h4>
            <CodeTipEl sel='.example'>
              <UserListEl
                attrs={{
                  users: [
                    {'distinct-id': `1`, type: UserType.Tracked},
                    {'distinct-id': `2`, type: UserType.Tracked},
                    {'distinct-id': `3`, type: UserType.Tracked},
                    {'distinct-id': `4`, type: UserType.Tracked},
                    {'distinct-id': `5`, type: UserType.Tracked},
                  ],
                  'max-icons-displayed': 4,
                }}
              />
            </CodeTipEl>
          </div>
          <div sel='.avatar-example'>
            <h4 sel='.example-title'>Recently Viewed</h4>
            <CodeTipEl sel='.example'>
              <UserListEl
                attrs={{
                  users: [
                    {
                      id: 1,
                      name: `First User`,
                      'img-src': avatarXl,
                      'label-prefix': `Created by`,
                      lastViewedUtcSeconds: DATE_IN_SECONDS - HOURS * 2,
                    },
                    {
                      id: 2,
                      name: `Second User`,
                      'img-src': avatarLg,
                      lastViewedUtcSeconds: DATE_IN_SECONDS,
                    },
                    {
                      id: 3,
                      name: `This UserNameIsTooLongToFitSoWeShouldTruncate`,
                      'img-src': avatarMd,
                      lastViewedUtcSeconds: DATE_IN_SECONDS - HOURS * 10,
                    },
                    {
                      id: 4,
                      name: `Fourth User`,
                      'img-src': avatarSm,
                      lastViewedUtcSeconds: DATE_IN_SECONDS - HOURS * 50,
                    },
                    {id: 5, name: `Other User`, lastViewedUtcSeconds: DATE_IN_SECONDS - HOURS * 24 * 750},
                  ],
                  'max-icons-displayed': 1,
                  'more-text': ``,
                  'truncated-tooltip-header': `Recently Viewed By:`,
                }}
              />
            </CodeTipEl>
          </div>
        </div>
      </SubSection>
      <SubSection title='mp-avatar-upload' autoDoc={true}>
        <p sel='.desc'>
          In Personal Settings under the “Your Profile” tab, collaborators may upload and edit their profile photo.
        </p>
        <p sel='.desc'>
          Clicking the Edit button should open something equivalent to a user’s finder window where they may select a
          supported image file type. Then we will allow users to zoom in/out to resize and crop their profile photo.
        </p>
        <CodeTipEl sel='.example'>
          <AvatarUploadEl
            params={{
              name: `Tony Stark`,
              id: 1,
            }}
          />
        </CodeTipEl>
      </SubSection>
    </Section>
  );
}
